
import axios from 'axios'

import { API_BASE_URL } from '@/config'
import {
  forgotPasswordRequestSchema,
  resetPasswordRequestSchema,
  type ForgotPasswordRequest,
  type ResetPasswordRequest,
  loginRequestSchema,
  workspaceLoginRequestSchema,
  loginResponseSchema,
  workspaceLoginResponseSchema,
  createAdminUserRequestSchema,
  createAdminUserResponseSchema,
  registerAndAcceptInviteRequestSchema,
  loginAndAcceptInviteRequestSchema,
  acceptInviteAuthResponseSchema,
  type LoginRequest,
  type WorkspaceLoginRequest,
  type LoginResponse,
  type WorkspaceLoginResponse,
  type RefreshResponse,
  type CreateAdminUserRequest,
  type CreateAdminUserResponse,
  type RegisterAndAcceptInviteRequest,
  type LoginAndAcceptInviteRequest,
  type AcceptInviteAuthResponse,
} from '@/types/api/auth'

import { apiClient, refreshSession, ApiError } from './client'

// Recovery must work independently of an expired access token, refresh cookie or auth interceptor.
const recoveryClient = axios.create({ baseURL: API_BASE_URL, withCredentials: false })

async function recoveryPost(path: string, data: unknown): Promise<void> {
  try {
    await recoveryClient.post(path, data)
  } catch (error) {
    if (axios.isAxiosError(error)) throw ApiError.fromAxiosError(error)
    throw error
  }
}

export function requestPasswordReset(data: ForgotPasswordRequest): Promise<void> {
  return recoveryPost('/auth/forgot-password', forgotPasswordRequestSchema.parse(data))
}

export function resetPassword(data: ResetPasswordRequest): Promise<void> {
  return recoveryPost('/auth/reset-password', resetPasswordRequestSchema.parse(data))
}

export async function login(data: LoginRequest): Promise<LoginResponse> {
  const validated = loginRequestSchema.parse(data)
  const response = await apiClient.post('/auth/login', validated)
  return loginResponseSchema.parse(response.data)
}

export async function workspaceLogin(data: WorkspaceLoginRequest): Promise<WorkspaceLoginResponse> {
  const validated = workspaceLoginRequestSchema.parse(data)
  const response = await apiClient.post('/auth/login/workspace', validated, {
    skipAuthRefresh: !!validated.loginToken,
  })
  return workspaceLoginResponseSchema.parse(response.data)
}

/** Refreshes the session and restores its identity in the stores; see {@link refreshSession}. */
export async function refreshToken(): Promise<RefreshResponse> {
  return refreshSession()
}

export async function logout(): Promise<void> {
  try {
    await apiClient.post('/auth/logout')
  } catch {
    // Ignore errors - logout should always proceed client-side
  }
}

export async function createAdminUser(data: CreateAdminUserRequest): Promise<CreateAdminUserResponse> {
  const validated = createAdminUserRequestSchema.parse(data)
  const response = await apiClient.post('/users/create-admin-user', validated)
  return createAdminUserResponseSchema.parse(response.data)
}

export async function registerAndAcceptInvite(data: RegisterAndAcceptInviteRequest): Promise<AcceptInviteAuthResponse> {
  const validated = registerAndAcceptInviteRequestSchema.parse(data)
  const response = await apiClient.post('/auth/register-and-accept-invite', validated)
  return acceptInviteAuthResponseSchema.parse(response.data)
}

export async function loginAndAcceptInvite(data: LoginAndAcceptInviteRequest): Promise<AcceptInviteAuthResponse> {
  const validated = loginAndAcceptInviteRequestSchema.parse(data)
  const response = await apiClient.post('/auth/login-and-accept-invite', validated)
  return acceptInviteAuthResponseSchema.parse(response.data)
}
